import { jitsiLocalStorage } from '@jitsi/js-utils';
import Delta from 'quill-delta';

import { getBackendSafeRoomName } from '../base/util';

import { NOTES_STORAGE_PREFIX, REDUCER_KEY } from './constants';
import logger from './logger';
import { decodeNotes, encodeNotes } from './storage';

/**
 * Returns this feature's root state.
 *
 * @param {Object} state - Global state.
 * @returns {Object} Feature state.
 */
const getState = state => state[REDUCER_KEY];

/**
 * Whether the notes pane is open.
 *
 * @param {Object} state - Global state.
 * @returns {boolean}
 */
export function getNotesPaneOpen(state) {
    return Boolean(getState(state)?.isOpen);
}

/**
 * Whether the notes modal is open (native).
 *
 * @param {Object} state - Global state.
 * @returns {boolean}
 */
export function getNotesModalOpen(state) {
    return Boolean(getState(state)?.isNotesModalOpen);
}

/**
 * Returns the in-memory notes text.
 *
 * @param {Object} state - Global state.
 * @returns {string}
 */
export function getNotesText(state) {
    return getState(state)?.text || '';
}

/**
 * Returns the backend-safe room name used to key private notes.
 *
 * @param {Object} state - Global state.
 * @returns {string}
 */
export function getNotesRoomName(state) {
    const room = state['features/base/conference']?.room;

    return getBackendSafeRoomName(room) || room || 'unknown';
}

/**
 * Builds the localStorage key for a room's private notes.
 *
 * @param {string|null} roomName - The conference room name.
 * @returns {string}
 */
export function getNotesStorageKey(roomName) {
    return `${NOTES_STORAGE_PREFIX}${roomName || 'unknown'}`;
}

/**
 * Loads private notes for a room from local storage.
 *
 * @param {string|null} roomName - The conference room name.
 * @returns {Object} Plain text, optional formatting, and storage status.
 */
export function loadConferenceNotes(roomName) {
    try {
        return { ...decodeNotes(jitsiLocalStorage.getItem(getNotesStorageKey(roomName)) || ''),
            saveStatus: 'saved' };
    } catch (error) {
        logger.warn('Failed to load conference notes', error);

        return { text: '',
            richText: null,
            saveStatus: 'loadError' };
    }
}

/**
 * Persists private notes for a room to local storage.
 *
 * @param {string} roomName - The conference room name.
 * @param {string} text - The notes text.
 * @param {Object|null} richText - Optional Quill document.
 * @returns {boolean} Whether storage succeeded.
 */
export function saveConferenceNotes(roomName, text, richText = null) {
    const key = getNotesStorageKey(roomName);

    try {
        if (text) {
            jitsiLocalStorage.setItem(key, encodeNotes(text, richText));
        } else {
            jitsiLocalStorage.removeItem(key);
        }

        return true;
    } catch (error) {
        logger.warn('Failed to save conference notes', error);

        return false;
    }
}

/**
 * A notes document as a Quill Delta. Plain-text documents become one insert.
 *
 * @param {Object} doc - The document.
 * @param {string} doc.text - Plain text.
 * @param {?Object} doc.richText - Optional Quill document.
 * @returns {Delta}
 */
function _toDelta({ richText, text }) {
    return new Delta(richText?.ops ?? [ { insert: `${text || ''}\n` } ]);
}

/**
 * Three-way merge of two concurrent edits of one notes document: our changes
 * (base → mine) are rebased onto theirs (base → theirs), theirs winning a tie.
 * A plain-text edit of a rich base (native) keeps the base's formatting.
 *
 * @param {Object} base - The common ancestor { text, richText }.
 * @param {Object} mine - Our edited document.
 * @param {Object} theirs - Their edited document.
 * @returns {Object} The merged { text, richText }.
 */
export function mergeNotes(base, mine, theirs) {
    const baseDelta = _toDelta(base);
    const mineChange = !mine.richText && base.richText
        ? _toDelta({ text: base.text }).diff(_toDelta({ text: mine.text }))
        : baseDelta.diff(_toDelta(mine));
    const merged = _toDelta(theirs).compose(baseDelta.diff(_toDelta(theirs)).transform(mineChange, true));
    const inserts = merged.ops.map(op => op.insert).filter(insert => typeof insert === 'string');
    const text = inserts.join('').slice(0, -1);

    return {
        richText: text && (mine.richText || theirs.richText) ? { ops: merged.ops } : null,
        text
    };
}
